import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { ApiError, api, type Me, unwrap } from "../api/client";
import { keys } from "../api/hooks";
import { PasskeySignIn } from "../components/passkeys/PasskeySignIn";
import { AuthFrame } from "../components/shell/AuthFrame";
import { Icon } from "../components/ui/primitives";
import { lang, t } from "../i18n";
import { inAppPath } from "../lib/links";

type Search = { next?: string };

/** The person, as a sign-in answers with it (`/api/me`'s shape). */
function isMe(body: unknown): body is Me {
  return Boolean(body && typeof body === "object" && "id" in body && "email" in body);
}

export const Route = createFileRoute("/signin")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    // One of the app's own pages only, as the email link's page checks it: "//host" and
    // "/\host" are other sites (review R10).
    next: typeof s.next === "string" ? (inAppPath(s.next) ?? undefined) : undefined,
  }),
  component: SignIn,
});

function SignIn() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "Resend the link" says it went (R10-S13): when, so a second press visibly does something.
  const [resentAt, setResentAt] = useState<string | null>(null);

  const requestLink = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // The link opens where the person was going (R10-N8).
      await unwrap(api.POST("/api/auth/magic-link", { body: next ? { email, next } : { email } }));
      if (sent)
        setResentAt(
          new Date().toLocaleTimeString(lang() === "vi" ? "vi-VN" : [], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        );
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("shell.couldntReach"));
    } finally {
      setBusy(false);
    }
  };

  // Signed in with a passkey (R8 task 3): into the app as a link sign-in goes, with the person
  // the server answered with, or asked for afresh.
  const enterApp = (body: unknown) => {
    if (isMe(body)) qc.setQueryData(keys.me, body);
    else qc.removeQueries({ queryKey: keys.me });
    void navigate({ href: next ?? "/" });
  };

  const signInWithCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await unwrap(api.POST("/api/auth/code", { body: { email, code } }));
      qc.setQueryData(keys.me, me);
      navigate({ href: next ?? "/" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("shell.couldntReach"));
    } finally {
      setBusy(false);
    }
  };

  if (!sent) {
    return (
      <AuthFrame>
        <form className="auth-card" onSubmit={requestLink}>
          <h2>{t("shell.signIn")}</h2>
          <p>{t("shell.signInIntro")}</p>
          <div className="fld">
            <label htmlFor="signin-email">{t("shell.email")}</label>
            <input
              className="inp"
              id="signin-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error && <div className="form-error">{error}</div>}
          <button
            type="submit"
            className="btn btn-primary btn-block"
            style={{ marginTop: 12 }}
            disabled={busy || !email.includes("@")}
          >
            <Icon name="i-mail" size="s" />
            {t("shell.emailMeLink")}
          </button>
          <PasskeySignIn onSignedIn={enterApp} />
          <p className="fine">{t("shell.onlyAdded")}</p>
        </form>
      </AuthFrame>
    );
  }

  // The address sits inside the sentence, in bold, wherever the language puts it.
  const [sentBefore = "", sentAfter = ""] = t("shell.linkSent").split("{email}");
  return (
    <AuthFrame>
      <div className="auth-card">
        <div className="mail-ic">
          <Icon name="i-mail" size="l" />
        </div>
        <h2>{t("shell.checkEmail")}</h2>
        <p>
          {sentBefore}
          <b>{email}</b>
          {sentAfter}
        </p>
        <form onSubmit={signInWithCode}>
          <div className="fld">
            <label htmlFor="signin-code">{t("shell.codeLabel")}</label>
            <input
              className="inp"
              id="signin-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          {error && <div className="form-error">{error}</div>}
          <button
            type="submit"
            className="btn btn-primary btn-block"
            style={{ marginTop: 12 }}
            disabled={busy || code.length !== 6}
          >
            {t("shell.signInWithCode")}
          </button>
        </form>
        <button
          type="button"
          className="btn btn-secondary btn-block"
          style={{ marginTop: 8 }}
          disabled={busy}
          onClick={() => requestLink()}
        >
          {t("shell.resendLink")}
        </button>
        {resentAt && (
          <p className="form-note" role="status">
            {t("shell.sentAgainAt", { time: resentAt })}
          </p>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-block"
          style={{ marginTop: 8 }}
          onClick={() => {
            setSent(false);
            setCode("");
            setError(null);
          }}
        >
          {t("shell.differentEmail")}
        </button>
      </div>
    </AuthFrame>
  );
}
